import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { getStripeStatus } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 8;

export default function SuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | expired | timeout
  const [details, setDetails] = useState(null);
  const attemptsRef = useRef(0);
  const { dispatch } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    let timer;

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const res = await getStripeStatus(sessionId);
        if (cancelled) return;
        setDetails(res);
        if (res.payment_status === "paid") {
          setState("paid");
          dispatch({ type: "clear" });
          return;
        }
        if (res.status === "expired") {
          setState("expired");
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("timeout");
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (cancelled) return;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("failed");
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL);
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, dispatch]);

  const amount = details ? (details.amount_total / 100).toFixed(2) : null;

  return (
    <div
      data-testid="success-page"
      className="relative min-h-screen bg-ink-950 text-white grain flex items-center justify-center px-6 py-24 overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120vw] h-[120vw] rounded-full bg-gradient-to-br from-gold/10 via-transparent to-crimson/10 blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="relative max-w-xl w-full text-center"
      >
        <div className="mb-8 flex justify-center">
          {state === "checking" && (
            <Loader2 size={56} className="text-gold animate-spin" data-testid="status-checking" />
          )}
          {state === "paid" && (
            <CheckCircle2 size={64} className="text-gold animate-pulse-glow" data-testid="status-paid" />
          )}
          {(state === "failed" || state === "expired" || state === "timeout") && (
            <XCircle size={56} className="text-crimson-glow" data-testid="status-error" />
          )}
        </div>

        <div className="text-xs tracking-[0.4em] uppercase text-cyan-glow mb-3">
          {state === "checking" && "◢ Verifying"}
          {state === "paid" && "◢ Confirmed"}
          {state === "failed" && "◢ Payment Issue"}
          {state === "expired" && "◢ Session Expired"}
          {state === "timeout" && "◢ Still Processing"}
        </div>

        <h1 className="font-display font-black uppercase text-4xl md:text-6xl leading-[0.9] tracking-tighter mb-6">
          {state === "checking" && "Reading the tide…"}
          {state === "paid" && <span className="text-shimmer-gold">Tsunami Secured.</span>}
          {state === "failed" && "Something broke."}
          {state === "expired" && "Session timed out."}
          {state === "timeout" && "Hang tight…"}
        </h1>

        <p className="font-serif italic text-lg text-slate-300 max-w-md mx-auto mb-10">
          {state === "checking" && "Holding the line while Stripe confirms your order."}
          {state === "paid" && amount && (
            <>
              Charged <span className="text-gold not-italic font-bold">${amount}</span>. A
              receipt is on its way to your inbox. The drop ships within 5–7 business days.
            </>
          )}
          {state === "failed" &&
            "We couldn't confirm payment. If you were charged, contact management@cassiusgrate.com with this reference."}
          {state === "expired" && "Your checkout session expired. Try again from the cart."}
          {state === "timeout" &&
            "Stripe is taking a moment. Check your email — a receipt will arrive once payment clears."}
        </p>

        {sessionId && (
          <div className="font-mono text-[10px] text-slate-500 mb-10 break-all">
            REF · {sessionId}
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            data-testid="back-home"
            className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-ink-950 font-black uppercase tracking-[0.25em] text-sm px-8 py-4"
          >
            Back to Site
          </Link>
          {state !== "paid" && (
            <Link
              to="/#merch"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-cyan-glow hover:text-cyan-glow font-bold uppercase tracking-[0.2em] text-sm px-6 py-4"
            >
              Return to Merch
            </Link>
          )}
        </div>
      </motion.div>
    </div>
  );
}
